"use client";

import { useCallback, useEffect } from "react";
import { atom, useAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { useParams } from "next/navigation";

import { isValidOrgId } from "@/lib/utils";
import type { ProductFilter } from "@/types/product-facet";
import { productFilterStorageKey } from "@/types/product-facet";

const RESERVED_PRODUCT_ROUTE_SEGMENTS = new Set(["new", "first"]);

function isRoutableProductId(value: string | undefined): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    !RESERVED_PRODUCT_ROUTE_SEGMENTS.has(value)
  );
}

function parseStoredFilter(raw: string | null): ProductFilter {
  if (!raw) return "all";
  try {
    const parsed = JSON.parse(raw) as ProductFilter | "unassigned";
    if (parsed === "all" || parsed === "unassigned") return "all";
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof parsed.productId === "string" &&
      parsed.productId.length > 0
    ) {
      return { productId: parsed.productId };
    }
  } catch {
    // ignore invalid stored value
  }
  return "all";
}

function filtersEqual(a: ProductFilter, b: ProductFilter): boolean {
  if (a === b) return true;
  if (typeof a === "object" && typeof b === "object") {
    return a.productId === b.productId;
  }
  return false;
}

function readStoredFilter(orgId: string): ProductFilter {
  if (typeof window === "undefined") return "all";
  try {
    return parseStoredFilter(
      localStorage.getItem(productFilterStorageKey(orgId)),
    );
  } catch {
    return "all";
  }
}

function persistFilter(orgId: string, filter: ProductFilter): void {
  try {
    localStorage.setItem(
      productFilterStorageKey(orgId),
      JSON.stringify(filter),
    );
  } catch {
    // ignore write errors
  }
}

/** Shared per-org filter so ProductSelector and Sidebar stay in sync. */
const productFilterAtomFamily = atomFamily((orgId: string) =>
  atom<ProductFilter>(readStoredFilter(orgId)),
);

const noopFilterAtom = atom<ProductFilter>("all");

export function useProductFilter(): {
  productFilter: ProductFilter;
  setProductFilter: (filter: ProductFilter) => void;
  isReady: boolean;
} {
  const { orgId, productId: routeProductId } = useParams();
  const validOrgId = isValidOrgId(orgId as string | undefined)
    ? (orgId as string)
    : undefined;

  const filterAtom = validOrgId
    ? productFilterAtomFamily(validOrgId)
    : noopFilterAtom;

  const [productFilter, setProductFilterState] = useAtom(filterAtom);

  const setProductFilter = useCallback(
    (filter: ProductFilter): void => {
      setProductFilterState(filter);
      if (validOrgId) {
        persistFilter(validOrgId, filter);
      }
    },
    [setProductFilterState, validOrgId],
  );

  // Hydrate from localStorage when org changes (atomFamily init runs once per org).
  useEffect(() => {
    if (!validOrgId) return;
    const stored = readStoredFilter(validOrgId);
    setProductFilterState((current) =>
      filtersEqual(current, stored) ? current : stored,
    );
  }, [validOrgId, setProductFilterState]);

  // Keep filter in sync when viewing a product detail page (direct nav, hub cards, etc.)
  useEffect(() => {
    if (!validOrgId) return;
    const productIdParam = routeProductId as string | undefined;
    if (!isRoutableProductId(productIdParam)) return;

    const routeFilter: ProductFilter = { productId: productIdParam };
    setProductFilterState((current) => {
      if (filtersEqual(current, routeFilter)) return current;
      persistFilter(validOrgId, routeFilter);
      return routeFilter;
    });
  }, [validOrgId, routeProductId, setProductFilterState]);

  return {
    productFilter,
    setProductFilter,
    isReady: true,
  };
}
